import * as Sentry from '@sentry/react-native';
import { logger as createLogger } from 'react-native-logs';
import { isExpectedAppError } from './sentryFilters';

const config = {
  severity: __DEV__ ? 'debug' : 'warn',
  async: true,
  dateFormat: 'time',
  printLevel: true,
  printDate: true,
  enabled: true,
};

const baseLogger = createLogger.createLogger(config);

// Turn an Error into a plain object so Sentry can display it in extras
const serializeError = (err: Error) => ({ name: err.name, message: err.message });

// Find an Error from args – checks direct args first, then inside { error } / { err }
const extractError = (args: any[]): Error | undefined => {
  const direct = args.find((a) => a instanceof Error);
  if (direct) return direct;

  for (const arg of args) {
    if (arg && typeof arg === 'object' && !(arg instanceof Error)) {
      if (arg.error instanceof Error) return arg.error;
      if (arg.err instanceof Error) return arg.err;
    }
  }
  return undefined;
};

// Sanitize args so Error instances serialize nicely in Sentry extras
const buildExtraData = (args: any[]): any[] =>
  args.map((arg) => {
    if (arg instanceof Error) return serializeError(arg);
    if (arg && typeof arg === 'object') {
      const clone: Record<string, any> = { ...arg };
      if (clone.error instanceof Error) clone.error = serializeError(clone.error);
      if (clone.err instanceof Error) clone.err = serializeError(clone.err);
      return clone;
    }
    return arg;
  });

// Expected failures (network drops, paywall, 404) are handled by the UI
// already. They go to Sentry as breadcrumbs so they show up as context on a
// real error, but never open an issue of their own. Only non-string args are
// classified: the leading message string may legitimately contain words like
// "Failed to fetch lesson data" without describing a transport failure.
const hasExpectedFailure = (args: any[]): boolean =>
  args.some((arg) => {
    if (!arg || typeof arg !== 'object') return false;
    if (isExpectedAppError(arg)) return true;
    if (arg instanceof Error) return false;
    return isExpectedAppError(arg.error) || isExpectedAppError(arg.err);
  });

const addLogBreadcrumb = (level: 'warning' | 'error', message: string, args: any[]) => {
  try {
    Sentry.addBreadcrumb({
      category: 'app.log',
      level,
      message,
      data: { args: buildExtraData(args.slice(1)) },
    });
  } catch {
    // Sentry might not be initialized yet
  }
};

export const logger = {
  debug: (...args: any[]) => {
    if (__DEV__) {
      baseLogger.debug(...args);
    }
  },
  info: (...args: any[]) => {
    baseLogger.info(...args);
  },
  warn: (...args: any[]) => {
    baseLogger.warn(...args);
    try {
      const firstArg = args[0];
      const message = typeof firstArg === 'string' ? firstArg : 'Application Warning';
      if (hasExpectedFailure(args)) {
        addLogBreadcrumb('warning', message, args);
        return;
      }
      const errorObject = extractError(args);

      if (errorObject) {
        Sentry.captureException(errorObject, {
          level: 'warning',
          extra: {
            contextMessage: message,
            data: buildExtraData(args.filter((a) => a !== errorObject)),
          },
        });
      } else {
        Sentry.captureMessage(message, {
          level: 'warning',
          extra: {
            additionalInfo: buildExtraData(args.slice(1)),
          },
        });
      }
    } catch {
      // Sentry might not be initialized yet
    }
  },
  error: (...args: any[]) => {
    baseLogger.error(...args);

    try {
      const firstArg = args[0];
      const message = typeof firstArg === 'string' ? firstArg : 'Application Error';
      if (hasExpectedFailure(args)) {
        addLogBreadcrumb('error', message, args);
        return;
      }
      const errorObject = extractError(args);

      if (errorObject) {
        Sentry.captureException(errorObject, {
          extra: {
            contextMessage: message,
            data: buildExtraData(args.filter((a) => a !== errorObject)),
          },
        });
      } else {
        Sentry.captureMessage(message, {
          level: 'error',
          extra: {
            additionalInfo: buildExtraData(args.slice(1)),
          },
        });
      }
    } catch (e) {
      console.error('Sentry failed to capture error:', e);
    }
  },
};

export default logger;
